import { useEffect, useState, type ReactNode } from 'react'
import { getApiClient } from '@/services'
import { useAuthContext } from '@/hooks'
import type { UiConfig } from '@/types'
import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlagsContext,
} from './featureFlagsContext'

/**
 * Fetches the member-chat UI feature flags from GET /config/ui once on mount and
 * exposes them via context. The endpoint is public; flags are backend-driven so
 * they toggle per-environment without a frontend rebuild. On error (or while
 * loading) the flags stay at their fail-closed defaults (all off), so prompt-pack
 * UI never flashes on before the server confirms it's enabled.
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { getToken } = useAuthContext()
  const [flags, setFlags] = useState<UiConfig>(DEFAULT_FEATURE_FLAGS)

  useEffect(() => {
    const controller = new AbortController()
    getApiClient(getToken)
      .getUiConfig(controller.signal)
      .then(setFlags)
      .catch((err) => {
        if (controller.signal.aborted) return
        console.error('Failed to load UI feature flags; defaulting to off', err)
      })
    return () => controller.abort()
  }, [getToken])

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}
